import React from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import _ from 'lodash';
import cn from 'classnames';
import './HomeShopFeatures.scss';

import { openNewCircleModal } from '../../../Actions/UIActions';
import { syncShopFeatures } from '../../../Actions/FeaturedActions';

import { getShopFeaturedResultsForType } from '../../../Helpers/featured_helpers';
import { getMarketingDescriptionForType } from '../../../Helpers/shop_helpers';
import ShopTile from '../../Shop/ShopTile';

const HomeShopFeatures = props => {
  // Core Data
  const data =
    {
      nyfw: {
        header: 'Fashion Show',
        subheader: 'Discover the most loved pieces from some of our brand partners showing in New York this Fashion Week.',
        numFeatures: 8,
        actions: [
          {
            display: 'Shop All Brands',
            link: '/shop/discover/brands?section=NYFW',
            className: 'primary'
          }
        ],
        results: getShopFeaturedResultsForType(props.featured, 'brands', 'NYFW')
      },
      curators: {
        header: 'Curator',
        subheader: getMarketingDescriptionForType('curators'),
        showcaseLargeFeature: true,
        numFeatures: 5,
        additionalRowNumFeatures: 4,
        showAllLink: '/shop/discover/curators',
        actions: [
          {
            display: 'Shop All Curators',
            link: '/shop/discover/curators',
            className: 'primary'
          }
        ],
        results: getShopFeaturedResultsForType(props.featured, 'curators')
      },
      groups: {
        header: 'Circle',
        subheader: getMarketingDescriptionForType('circles'),
        numFeatures: 8,
        showAllLink: '/shop/discover/circles',
        actions: [
          {
            display: 'Create Circle',
            onClick: () => props.openNewCircleModal({ source: 'Home Featured Circles' })
          },
          {
            display: 'Shop All Circles',
            link: '/shop/discover/circles',
            className: 'primary'
          }
        ],
        results: getShopFeaturedResultsForType(props.featured, 'circles')
      },
      catalog: {
        header: 'Category',
        subheader: getMarketingDescriptionForType('catalog'),
        numFeatures: 8,
        showAllLink: '/shop/discover/categories',
        actions: [
          {
            display: 'Shop All Categories',
            link: '/shop/discover/categories',
            className: 'primary'
          }
        ],
        results: getShopFeaturedResultsForType(props.featured, 'catalog')
      },
      brands: {
        header: 'Brand',
        subheader: getMarketingDescriptionForType('brands'),
        numFeatures: 8,
        showAllLink: '/shop/discover/brands',
        actions: [
          {
            display: 'Shop All Brands',
            link: '/shop/discover/brands',
            className: 'primary'
          }
        ],
        results: getShopFeaturedResultsForType(props.featured, 'brands', undefined, 'NYFW') // exclude NYFW from general brands
      }
    }[props.type] || {};
  const { header, subheader, actions, results, showcaseLargeFeature, numFeatures, additionalRowNumFeatures } = data;

  // UI handling
  const additionalClasses = { 'showcase-large-feature': showcaseLargeFeature };

  return (
    <div ref={props.containerRef} className='home-shop-features-outer-container'>
      <div className='home-shop-features-inner-container'>
        <div className='header-section'>
          <div className='main'>
            <div className='shop-by'>Shop by</div>
            <div className='header'>{header}</div>
          </div>
          <div className='secondary'>
            <div className='subheader'>{subheader}</div>
            {!!actions?.length && (
              <div className='actions'>
                {actions.map(action => {
                  const { link, onClick, display, className } = action;
                  return link ? (
                    <Link key={display} to={link} className={cn('action', className)}>
                      {display}
                    </Link>
                  ) : (
                    <div onClick={onClick} key={display} className={cn('action', className)}>
                      {display}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className={cn('features-section', additionalClasses)}>
          {showcaseLargeFeature && (
            <div className={cn('features-subsection large-feature', { loading: !results?.[0] })}>
              <div className='feature hide-on-small'>
                {results?.[0] && (
                  <ShopTile featured={props.featured} feature={results?.[0]} syncShopFeatures={props.syncShopFeatures} eventSource='Home Featured' />
                )}
              </div>
            </div>
          )}
          <div className={cn('features-subsection grid', additionalClasses)}>
            {_.range(numFeatures).map(i => {
              const feature = results?.[i];
              const isLastFeature = i === numFeatures - 1;
              return (
                <div
                  key={i}
                  className={cn('feature', {
                    loading: !feature,
                    'hide-on-large': i === 0 && showcaseLargeFeature,
                    'hide-on-small': isLastFeature && showcaseLargeFeature
                  })}
                >
                  {feature && (
                    <ShopTile featured={props.featured} feature={feature} syncShopFeatures={props.syncShopFeatures} eventSource='Home Featured' />
                  )}
                </div>
              );
            })}
          </div>
          {additionalRowNumFeatures && (
            <>
              <div className={cn('features-subsection additional-features-grid', additionalClasses)}>
                {_.range(additionalRowNumFeatures)
                  .slice(0, 3) // Everything shifts by 1 on large
                  .map(i => {
                    const feature = results?.[numFeatures + i - 1];
                    return (
                      <div
                        key={i}
                        className={cn('feature half-size-feature', {
                          loading: !feature,
                          'hide-on-large': i === 0 && showcaseLargeFeature,
                          'hide-on-small': i === 2 && showcaseLargeFeature
                        })}
                      >
                        {feature && (
                          <ShopTile
                            featured={props.featured}
                            feature={feature}
                            syncShopFeatures={props.syncShopFeatures}
                            eventSource='Home Featured'
                          />
                        )}
                      </div>
                    );
                  })}
              </div>
              <div className={cn('features-subsection additional-features-grid', additionalClasses)}>
                {_.range(additionalRowNumFeatures + 1)
                  .slice(2, 5)
                  .map(i => {
                    const feature = results?.[numFeatures + i - 1];
                    return (
                      <div
                        key={i}
                        className={cn('feature', {
                          loading: !feature,
                          'hide-on-large': i === 2 && showcaseLargeFeature,
                          'hide-on-small': i >= 4 && showcaseLargeFeature
                        })}
                      >
                        {feature && (
                          <ShopTile
                            featured={props.featured}
                            feature={feature}
                            syncShopFeatures={props.syncShopFeatures}
                            eventSource='Home Featured'
                          />
                        )}
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </div>
        {!!actions.length && (
          <div className='actions-mobile'>
            {actions.map(action => {
              const { link, onClick, display, className } = action;
              return link ? (
                <Link key={display} to={link} className={cn('action', className)}>
                  {display}
                </Link>
              ) : (
                <div onClick={onClick} key={display} className={cn('action', className)}>
                  {display}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

HomeShopFeatures.propTypes = {
  // From Outside
  type: PropTypes.oneOf(['nyfw', 'curators', 'groups', 'brands', 'catalog']).isRequired,
  containerRef: PropTypes.object, // If needed to scroll to this component

  // From Inside
  featured: PropTypes.object.isRequired,
  syncShopFeatures: PropTypes.func.isRequired,
  openNewCircleModal: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { featured } = state;
  return { featured };
};

export default connect(mapStateToProps, {
  syncShopFeatures,
  openNewCircleModal
})(HomeShopFeatures);
